import type { CreateCustomerContactInput, UpdateCustomerContactInput, UpdateCustomerInput } from '@chukua/common';
import { useState } from 'react';
import { ApiError } from '../../lib/api';
import { useToast } from '../../lib/use-toast';
import { activateCustomer, addCustomerContact, deactivateCustomer, removeCustomerContact, updateCustomer, updateCustomerContact } from './customers.api';
import { useCustomerRecord } from './use-customer-record';

/** A customer's own page: its record (`useCustomerRecord`) plus every mutation the Details tab needs. */
export function useCustomerDetail(id: string) {
  const { state, reload } = useCustomerRecord(id);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  async function update(input: UpdateCustomerInput): Promise<boolean> {
    setUpdateError(null);
    setSaving(true);
    try {
      await updateCustomer(id, input);
      await reload();
      toast.success('Customer updated');
      return true;
    } catch (err) {
      setUpdateError(
        err instanceof ApiError && err.status === 409 ? 'A customer with this KRA PIN already exists.' : 'Could not save — check the fields and try again.',
      );
      return false;
    } finally {
      setSaving(false);
    }
  }

  /** A row action with no form of its own: reload and toast on success, toast.error on failure (CLAUDE.md → Web conventions). */
  async function rowAction(action: () => Promise<void>, successMessage: string, errorMessage: string): Promise<void> {
    try {
      await action();
      await reload();
      toast.success(successMessage);
    } catch {
      toast.error(errorMessage);
    }
  }

  const activate = () => rowAction(() => activateCustomer(id), 'Customer activated', "Couldn't activate the customer");
  const deactivate = () => rowAction(() => deactivateCustomer(id), 'Customer deactivated', "Couldn't deactivate the customer");
  const addContact = (input: CreateCustomerContactInput) => rowAction(() => addCustomerContact(id, input), `${input.name} added`, `Couldn't add ${input.name}`);
  const updateContact = (contactId: string, input: UpdateCustomerContactInput) =>
    rowAction(() => updateCustomerContact(id, contactId, input), `${input.name} updated`, `Couldn't update ${input.name}`);
  const removeContact = (contactId: string, name: string) =>
    rowAction(() => removeCustomerContact(id, contactId), `${name} removed`, `Couldn't remove ${name}`);

  return { state, updateError, saving, update, activate, deactivate, addContact, updateContact, removeContact };
}
